import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Star, ShoppingCart, Heart, Cpu, Trash2 } from 'lucide-react';
import gsap from 'gsap';
import { setSelectedGameId, setSelectedGame } from '../../redux/slice/game.slice';
import { toggleWishlist, addToCart } from '../../redux/slice/user.slice';
import { getGamePath } from '../../utils/gamePath';
import ProgressiveImage from './ProgressiveImage';
import { PlatformIcon, PlatformColor, PlatformLabel } from './GameCard';



const GameListCard = React.memo(({ game, showFilters, onAddToCart, isWishlistPage = false, className = '', style = {} }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { wishlist } = useSelector((s) => s.userSlice || { wishlist: [] });
    const targetId = game?.id || game?._id;
    const isFav = wishlist?.some(id => (id?._id || id?.id || id) === targetId);
    const cardRef = useRef(null);

    useEffect(() => {
        const isReturning = Boolean(
            sessionStorage.getItem('cg_scroll_/all-games') ||
            sessionStorage.getItem('cg_scroll_/platforms') ||
            sessionStorage.getItem('cg_last_game_id_/all-games') ||
            sessionStorage.getItem('cg_last_game_id_/platforms')
        );
        if (isReturning) return;

        if (cardRef.current) {
            gsap.fromTo(cardRef.current,
                { opacity: 0, y: 20 },
                { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', clearProps: 'transform,opacity' }
            );
        }
    }, []);

    const handleCardClick = useCallback((e) => {
        e.preventDefault();
        if (!game) return;
        const currentPath = window.location.pathname;
        const currentScroll = (window.__lenis ? window.__lenis.scroll : null) ?? window.scrollY ?? 0;
        if (currentScroll > 0) {
            sessionStorage.setItem(`cg_scroll_${currentPath}`, String(currentScroll));
        }
        if (targetId) {
            sessionStorage.setItem(`cg_last_game_id_${currentPath}`, String(targetId));
        }
        dispatch(setSelectedGameId(targetId));
        dispatch(setSelectedGame(game));
        navigate(getGamePath(game));
    }, [dispatch, navigate, game, targetId]);

    const handleWishlistClick = useCallback((e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (game) {
            dispatch(toggleWishlist(game));
        }
    }, [dispatch, game]);

    const handleBuyClick = useCallback((e) => {
        e.stopPropagation();
        if (!game) return;
        if (typeof onAddToCart === 'function') {
            onAddToCart(game);
        } else {
            const available = Object.entries(game.platforms || {}).filter(([_, info]) => info?.available);
            const selectedPlatform = available.length > 0 ? available[0][0] : 'windows';
            dispatch(addToCart({ ...game, selectedPlatform }));
        }
    }, [dispatch, game, onAddToCart]);

    if (!game) return null;

    const firstPlat = Object.values(game.platforms || {}).find(p => p?.available);
    const displayOriginal = game.platforms?.windows?.available
        ? game.platforms.windows.originalPrice
        : (firstPlat !== undefined ? firstPlat.originalPrice : game.originalPrice || 0);

    const displayPrice = game.platforms?.windows?.available
        ? game.platforms.windows.price
        : (firstPlat !== undefined ? firstPlat.price : game.price || 0);

    return (
        <div
            ref={cardRef}
            id={`game-card-${targetId}`}
            data-game-id={targetId}
            onClick={handleCardClick}
            style={style}
            className={`group relative flex flex-col md600:flex-row cursor-pointer bg-[#080814]/85 backdrop-blur-xl cyber-card-glow transition-all duration-300 overflow-hidden w-full ${className}`}
        >
            {/* Left Image Box */}
            <div className="relative w-full md600:w-48 lg:w-60 aspect-[16/10] overflow-hidden flex-shrink-0 cursor-pointer">
                <ProgressiveImage
                    src={game.cover_image?.url || game.image}
                    placeholderSrc={game.cover_image?.lowUrl || game.image}
                    alt={game.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />

                {game.discount > 0 && (
                    <div className="absolute top-3 left-3 px-2.5 py-0.5 rounded-md bg-cyber-green text-cyber-black font-cyber font-extrabold text-[9px] tracking-wider shadow-[0_0_12px_rgba(57,255,20,0.5)] z-10">
                        -{game.discount}%
                    </div>
                )}

                {/* Rating Badge Overlay at Bottom Left (Same as GameCard) */}
                <div className="absolute bottom-2 left-2.5 flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/65 backdrop-blur-md border border-white/15 text-xs font-mono text-white font-bold shadow-md z-10">
                    <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400 drop-shadow-[0_0_6px_rgba(250,204,21,0.6)]" />
                    <span>{Number(game.rating || 4.9).toFixed(1)}</span>
                </div>

                <button
                    type="button"
                    onClick={handleWishlistClick}
                    aria-label={isWishlistPage ? "Remove from wishlist" : (isFav ? "Remove from wishlist" : "Add to wishlist")}
                    title={isWishlistPage ? "Remove from wishlist" : (isFav ? "Remove from wishlist" : "Add to wishlist")}
                    className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-black/65 backdrop-blur-md border border-white/15 flex items-center justify-center text-gray-400 hover:text-cyber-pink hover:border-cyber-pink/60 hover:bg-cyber-pink/20 hover:shadow-[0_0_12px_rgba(255,0,122,0.6)] transition-all duration-300 z-10"
                >
                    {isWishlistPage ? (
                        <Trash2 className="w-3.5 h-3.5 text-cyber-pink drop-shadow-[0_0_6px_rgba(255,0,122,0.6)]" />
                    ) : (
                        <Heart className={`w-3.5 h-3.5 ${isFav ? 'fill-cyber-pink text-cyber-pink drop-shadow-[0_0_8px_rgba(255,0,122,0.8)]' : ''}`} />
                    )}
                </button>
            </div>

            {/* Content Section */}
            <div className="p-4 sm:p-5 flex flex-col justify-between flex-1 gap-2.5 min-w-0">
                <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-[9px] font-cyber font-bold uppercase tracking-widest text-cyber-purple bg-cyber-purple/15 px-2.5 py-0.5 rounded-md border border-cyber-purple/30 group-hover:border-cyber-purple/60 group-hover:shadow-[0_0_10px_rgba(189,0,255,0.3)] transition-all truncate">
                            {game.category?.name || 'GAME'}
                        </span>
                        {/* Platform Icons in header row matching GameCard */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                            {Object.entries(PlatformIcon).map(([plat, Icon]) => {
                                if (!game.platforms?.[plat]?.available) return null;
                                return (
                                    <Icon
                                        key={plat}
                                        className="w-3.5 h-3.5 opacity-80 group-hover:opacity-100 transition-opacity"
                                        style={{ color: PlatformColor[plat] }}
                                        title={PlatformLabel[plat]}
                                    />
                                );
                            })}
                            {game.isVRReady && (
                                <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-cyber-purple/10 border border-cyber-purple/20 text-cyber-purple text-[9px] font-cyber">
                                    <Cpu className="w-3 h-3 animate-pulse" />
                                    <span className="uppercase tracking-wider">VR</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <h3
                        className="font-cyber font-bold text-sm sm:text-base text-white group-hover:text-cyber-cyan transition-colors duration-300 line-clamp-1 uppercase tracking-tight cursor-pointer"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const currentPath = window.location.pathname;
                            const currentScroll = (window.__lenis ? window.__lenis.scroll : null) ?? window.scrollY ?? 0;
                            if (currentScroll > 0) {
                                sessionStorage.setItem(`cg_scroll_${currentPath}`, String(currentScroll));
                            }
                            if (targetId) {
                                sessionStorage.setItem(`cg_last_game_id_${currentPath}`, String(targetId));
                            }
                            dispatch(setSelectedGameId(game.id || game._id));
                            dispatch(setSelectedGame(game));
                            navigate(getGamePath(game));
                        }}
                    >
                        {game.title}
                    </h3>

                    {(game?.description || game?.tagline) && (
                        <p className="text-gray-400 text-[11px] line-clamp-1 leading-relaxed font-sans">
                            {game.description || game.tagline}
                        </p>
                    )}
                </div>

                {/* Price & Action Section */}
                <div className="flex items-center justify-between pt-3.5 mt-auto border-t border-white/5 md600:border-t-0 md600:pt-0">
                    <div className="flex flex-col">
                        {game.discount > 0 && (
                            <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500 line-through font-mono tracking-tighter">
                                    ${Number(displayOriginal).toFixed(2)}
                                </span>
                                <span className="text-cyber-green text-[9px] font-cyber font-bold">-{game.discount}%</span>
                            </div>
                        )}
                        <span className="font-cyber font-black text-base sm:text-lg text-white transition-colors">
                            {displayPrice === 0 ? 'FREE' : `$${Number(displayPrice).toFixed(2)}`}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={handleBuyClick}
                        className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-cyber font-bold uppercase tracking-wider text-cyber-cyan bg-cyber-cyan/15 hover:bg-cyber-cyan/25 border border-cyber-cyan/40 hover:border-cyber-cyan/60 transition-all duration-200 active:scale-95 group/btn"
                    >
                        <ShoppingCart className="w-3.5 h-3.5 group-hover/btn:scale-110 transition-transform text-cyber-cyan" /> BUY
                    </button>
                </div>
            </div>
        </div>
    );
});

export const GameListCardSkeleton = React.memo(({ className = '', style = {} }) => (
    <div
        style={style}
        className={`relative flex flex-col md600:flex-row bg-[#080814]/85 backdrop-blur-xl overflow-hidden animate-pulse w-full ${className}`}
    >
        {/* Left Image Box */}
        <div className="relative w-full md600:w-48 lg:w-60 aspect-[16/10] overflow-hidden flex-shrink-0 bg-white/[0.04]">
            {/* Wishlist Button Placeholder */}
            <div className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-black/60 border border-white/15" />
        </div>

        {/* Content Section */}
        <div className="p-4 sm:p-5 flex flex-col justify-between flex-1 gap-2.5 min-w-0">
            <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <div className="h-4 w-16 sm:w-20 rounded-md bg-cyber-purple/20 border border-cyber-purple/30" />
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        <div className="h-3 w-16 bg-white/10 rounded" />
                    </div>
                </div>

                <div className="h-4 sm:h-5 w-3/4 bg-white/10 rounded mt-0.5" />
                <div className="h-3.5 w-full bg-white/5 rounded" />

                <div className="flex items-center gap-2 mt-1">
                    <div className="w-3.5 h-3.5 rounded-full bg-white/10" />
                    <div className="w-3.5 h-3.5 rounded-full bg-white/10" />
                    <div className="w-3.5 h-3.5 rounded-full bg-white/10" />
                </div>
            </div>

            {/* Price & Action Section */}
            <div className="flex items-center justify-between pt-3.5 mt-auto border-t border-white/5 md600:border-t-0 md600:pt-0">
                <div className="h-5 w-16 bg-white/10 rounded" />
                <div className="h-8 w-20 rounded-lg bg-cyber-cyan/20 border border-cyber-cyan/30" />
            </div>
        </div>
    </div>
));

export default GameListCard;

